import React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import {
  useNavigate,
  Outlet,
  redirect,
  useLocation,
} from "react-router-dom";
import * as Mui from "@mui/material";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";

import { DocContext, useMultilayerDoc } from "./hooks/useY";
import { AuthenticationResult, EventType } from "@azure/msal-browser";

import "@/styles/globals.css";
import Breadcrumbs from "./components/Breadcrumbs";

function AppWrapper() {
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const { instance } = useMsal();
  const doc = useMultilayerDoc();

  const location = useLocation();
  const isAuthenticationLocation = ["/login", "/auth-callback"].includes(location.pathname);

  useEffect(() => {
    if (!instance) return;

    instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        instance.setActiveAccount(account);
        redirect("/");
      }
    });
  }, [instance, navigate]);

  const handleAuthAction = () => {
    if (isAuthenticated) {
      instance.logout();
    } else {
      navigate("/login");
    }
  };

  const [paletteMode, setPaletteMode] = useState<Mui.PaletteMode>("light");
  const togglePaletteMode = useCallback(
    () =>
      setPaletteMode((prevMode) => (prevMode === "light" ? "dark" : "light")),
    []
  );

  const theme = useMemo(
    () =>
      Mui.createTheme({
        palette: {
          mode: paletteMode,
          secondary: {
            main: "#42B81A",
          },
        },
      }),
    [paletteMode]
  );

  return (
    <Mui.ThemeProvider theme={theme} >
      <Mui.CssBaseline />
      {/* AppBar with Breadcrumbs and Login/Logout Button */}
      <Mui.AppBar position="sticky" className="no-print">
        <Mui.Toolbar>
          {/* Breadcrumb Component */}
          <Breadcrumbs />

          {/* Spacer to push the button to the right */}
          <Mui.Box sx={{ flexGrow: 1 }} />
          <Mui.IconButton onClick={togglePaletteMode} color="inherit">
            {theme.palette.mode === "dark" ? (
              <Brightness7Icon />
            ) : (
              <Brightness4Icon />
            )}
          </Mui.IconButton>

          {/* Login/Logout Button */}
          <Mui.Button color="inherit" onClick={handleAuthAction}>
            {isAuthenticated ? "Logout" : "Login"}
          </Mui.Button>
        </Mui.Toolbar>
      </Mui.AppBar>

      {isAuthenticationLocation === false && doc === undefined && (
        <Mui.Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Mui.CircularProgress />
        </Mui.Box>
      )}
      {isAuthenticationLocation === false && doc !== undefined && (
        <DocContext.Provider value={doc}>
          <Outlet />
        </DocContext.Provider>
      )}

      {isAuthenticationLocation === true && <Outlet />}
    </Mui.ThemeProvider>
  );
}

export default React.memo(AppWrapper);
